// src/pages/student/StudentContent.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, getDocs, orderBy } from "firebase/firestore";
import { db, functions } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import StudentProfileForm from './StudentProfileForm';

const fetchPublicPortalDataCallable = httpsCallable(functions, 'fetchPublicPortalData');

// Local Metric Card
const MetricCard = ({ title, value, icon, color }) => {
    const valueDisplay = value === null || value === 'ERR' ? "N/A" : value.toLocaleString();
    return (
        <div className={`p-5 rounded-lg shadow-sm border-l-4 border-${color}-500 bg-white flex items-center space-x-4`}>
            <div className={`text-3xl p-3 rounded-full bg-${color}-50 text-${color}-600`}>{icon}</div>
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-800">{valueDisplay}</p>
            </div>
        </div>
    );
};
// Local Student Exam Card
function StudentExamCard({ exam, onStartExam, onResultView }) {
    const scheduledTimeMs = exam.scheduledTime?.seconds ? exam.scheduledTime.seconds * 1000 : exam.scheduledTime;
    const isFuture = scheduledTimeMs > Date.now();
    const isReady = !isFuture && exam.paperStatus !== 'results_published';
    const isResultPublished = exam.paperStatus === 'results_published';

    let buttonText = 'Access Denied';
    let buttonClass = 'bg-gray-500 cursor-not-allowed';

    if (isResultPublished) {
        buttonText = 'View Rank & Score';
        buttonClass = 'bg-purple-600 hover:bg-purple-700';
    } else if (isReady) {
        buttonText = 'Start Exam Now';
        buttonClass = 'bg-green-600 hover:bg-green-700';
    } else if (isFuture) {
        buttonText = 'Upcoming';
        buttonClass = 'bg-blue-400 cursor-not-allowed';
    }

    const handleClick = () => {
        if (isResultPublished) {
            onResultView(exam.paperId, exam.paperTitle);
        } else if (isReady) {
            onStartExam(exam);
        }
    };

    return (
        <div className="p-4 border rounded-lg shadow-md bg-white space-y-3">
            <h3 className="text-xl font-bold text-indigo-700">{exam.paperTitle || 'Untitled Exam'}</h3>
            <p className="text-sm text-gray-600">
                <span className="font-medium">Format:</span> {exam.paperFormat ? exam.paperFormat.toUpperCase() : 'N/A'} |
                <span className="font-medium"> Duration:</span> {exam.paperDuration || 'N/A'} min
            </p>
            <p className="text-sm">
                <span className="font-semibold">Date:</span> {scheduledTimeMs ? new Date(scheduledTimeMs).toLocaleString() : 'N/A'}
            </p>
            <p className="text-sm">
                <span className="font-semibold">Status:</span> {isResultPublished ? 'GRADED (Published)' : isReady ? 'ACTIVE' : isFuture ? 'UPCOMING' : 'PAST/MISSED'}
            </p>

            <button
                onClick={handleClick}
                disabled={!isReady && !isResultPublished}
                className={`w-full p-2 text-white rounded-md font-semibold ${buttonClass}`}
            >
                {buttonText}
            </button>
        </div>
    );
}


// ===================================================================
// COMPONENT: Student Dashboard Content
// ===================================================================
export default function StudentContent() {
    const { user, userProfile, fetchScheduledExams, startExamSession } = useAuth();
    const [tests, setTests] = useState([]);
    const [adverts, setAdverts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    const isProfileComplete = userProfile?.profileComplete === true;

    // --- Core Exam Loading Logic ---
    const loadExams = async () => {
        setMessage('');
        try {
            const fetchedExams = await fetchScheduledExams();
            setTests(fetchedExams);
        } catch (err) {
            console.error("Error fetching scheduled exams in loadExams:", err);
            setMessage(`Error loading exams: ${err.message}`);
            setTests([]);
        }
    };

    // --- Core Exam Starting Logic ---
    const handleStartExam = async (exam) => {
        setMessage(`Starting session for ${exam.paperTitle}...`);
        try {
            const sessionData = await startExamSession({
                registrationId: exam.registrationId,
                paperId: exam.paperId,
            });

            setMessage('Session started! Redirecting to exam interface.');
            navigate('/exam/take', { state: {
                submissionId: sessionData.submissionId,
                paperId: exam.paperId,
                format: exam.paperFormat,
                duration: exam.paperDuration
            }});
        } catch (err) {
            setMessage(`Error starting exam: ${err.message}`);
        }
    };

    // Handler for Result Viewing
    const handleResultView = (paperId, paperTitle) => {
        navigate('/student/results', { state: { paperId, paperTitle } });
    };


    // --- Data Fetching Effect (Scheduled Tests & Ads) ---
    useEffect(() => {
        if (!user?.uid || !isProfileComplete) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        const fetchStudentData = async () => {
            setLoading(true);
            try {
                // 1. Fetch scheduled tests
                await loadExams();

                // 2. Fetch and Filter Relevant Advertisements
                const result = await fetchPublicPortalDataCallable();
                const allAdverts = result.data.exams || [];

                const studentInterests = userProfile.examInterests;
                const studentCategory = studentInterests?.category;
                const studentGrade = studentInterests?.school?.grade?.toLowerCase() || '';
                const studentCompetitive = studentInterests?.competitive?.map(s => s.toLowerCase()).join('|');
                const studentEntrance = studentInterests?.entrance?.map(s => s.toLowerCase()).join('|');

                const filteredAdverts = allAdverts.filter(ad => {
                    if (ad.registrationStatus !== 'Ongoing' && ad.registrationStatus !== 'Details Available') return false;
                    const adTitle = ad.title.toLowerCase();

                    if (studentCategory === 'school' && studentGrade && adTitle.includes(studentGrade)) return true;
                    if (studentCategory === 'competitive' && studentCompetitive) {
                        if (studentCompetitive.split('|').some(keyword => adTitle.includes(keyword))) return true;
                    }
                    if (studentCategory === 'entrance' && studentEntrance) {
                        if (studentEntrance.split('|').some(keyword => adTitle.includes(keyword))) return true;
                    }
                    return false;
                });

                setAdverts(filteredAdverts);

            } catch (err) {
                console.error("StudentContent: Error fetching student exam data:", err);
                if (isMounted) setMessage(prev => prev || `Initial load failed: ${err.message}`);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchStudentData();
        return () => { isMounted = false; };
    }, [user.uid, isProfileComplete, userProfile]);

    if (!isProfileComplete) {
        return <StudentProfileForm />;
    }

    // Grouping logic based on isReady, isFuture, and paperStatus (for the Metric Cards)
    const activeExams = tests.filter(e => {
        const scheduledTimeMs = e.scheduledTime?.seconds ? e.scheduledTime.seconds * 1000 : e.scheduledTime;
        const isFuture = scheduledTimeMs > Date.now();
        return !isFuture && e.paperStatus !== 'results_published';
    });
    const upcomingExams = tests.filter(e => {
        const scheduledTimeMs = e.scheduledTime?.seconds ? e.scheduledTime.seconds * 1000 : e.scheduledTime;
        return scheduledTimeMs > Date.now();
    });
    const publishedResults = tests.filter(e => e.paperStatus === 'results_published');

    if (loading) return <p className="p-4 text-center text-gray-500">Loading Student Data...</p>;

    return (
        <div className="space-y-8">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex justify-between items-center">
                <div>
                    <p className="text-sm text-gray-600">Student ID: <span className="font-medium text-gray-900">{userProfile?.registrationNumber || 'N/A'}</span></p>
                    <p className="text-sm text-gray-600">Name: <span className="font-medium text-gray-900">{userProfile?.displayName || 'N/A'}</span></p>
                </div>
                <img src={userProfile?.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${userProfile?.displayName || 'U'}`} alt="Profile" className="w-12 h-12 rounded-full object-cover bg-gray-200 border"/>
            </div>

            {message && (
                <div className={`p-3 rounded-md font-medium ${message.startsWith('Error') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    {message}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard title="Upcoming Tests" value={upcomingExams.length} icon="📝" color="blue" />
                <MetricCard title="Active Exams" value={activeExams.length} icon="⏳" color="red" />
                <MetricCard title="Results Available" value={publishedResults.length} icon="🏆" color="green" />
                <MetricCard title="Courses Enrolled" value={0} icon="📚" color="indigo" />
            </div>

            {/* --- NEW: Relevant Exam Announcements --- */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Relevant Exam Announcements</h3>
                {adverts.length > 0 ? (
                    <ul className="space-y-3">
                        {adverts.map(ad => (
                            <li key={ad.id} className="p-4 border rounded-md flex justify-between items-center hover:bg-gray-50">
                                <div>
                                    <h4 className="font-semibold text-gray-900">{ad.title}</h4>
                                    <p className="text-sm text-gray-500">
                                        Status:
                                        <span className={`ml-1 font-semibold ${ad.registrationStatus === 'Ongoing' ? 'text-green-600' : 'text-yellow-600'}`}>
                                            {ad.registrationStatus}
                                        </span>
                                        {ad.isNew && <span className="ml-2 px-1.5 text-xs font-bold rounded-full bg-red-600 text-white">NEW</span>}
                                    </p>
                                </div>
                                <a href={ad.advertisementUrl} target="_blank" rel="noopener noreferrer" className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700">View Ad</a>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-center text-gray-500 py-4 italic">No relevant exam announcements right now. Complete your profile fully!</p>
                )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Your Exam Schedule</h3>
                {tests.length === 0 ? (
                    <p className="text-center text-gray-500 py-4 italic">You are not currently registered for any exams.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tests.map(test => (
                            <StudentExamCard
                                key={test.registrationId}
                                exam={test}
                                onStartExam={handleStartExam}
                                onResultView={handleResultView}
                            />
                        ))}
                    </div>
                )}
                <button onClick={() => loadExams().then(() => setLoading(false))} className="mt-4 text-sm text-indigo-600 hover:underline font-semibold">
                    Refresh Exam Schedule
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">My Courses</h3>
                    <p className="text-center text-gray-500 py-4 italic"><a href="/student/courses" className="text-indigo-600 hover:underline">Go to Course Catalog</a></p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Live Classes</h3>
                    <p className="text-center text-gray-500 py-4 italic">No active live sessions.</p>
                </div>
            </div>
        </div>
    );
}
